import { useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';
import { apiConfig } from './authConfig';

export interface UseRolesResult {
  roles: string[];
  isAdmin: boolean;
  isPowerUser: boolean;
  canWrite: boolean;
  isLoading: boolean;
}

function parseRolesFromJwt(accessToken: string): string[] {
  const tokenParts = accessToken.split('.');
  if (tokenParts.length !== 3) return [];

  try {
    // JWT uses base64url encoding (-/_), but atob expects standard base64 (+//).
    const payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decoded = JSON.parse(atob(paddedPayload));
    return Array.isArray(decoded.roles) ? decoded.roles : [];
  } catch {
    return [];
  }
}

/**
 * Hook that extracts the user's App Roles from the access token claims.
 * Roles are sourced from both the ID token and access token to cover all scenarios.
 */
export function useRoles(): UseRolesResult {
  const { instance, accounts, inProgress } = useMsal();
  const [roles, setRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const extractRoles = async () => {
      if (inProgress !== 'none' || !accounts[0]) {
        setIsLoading(inProgress !== 'none');
        return;
      }

      // First check ID token claims (available immediately)
      const idTokenRoles = (accounts[0]?.idTokenClaims as Record<string, unknown>)?.roles;
      const idRoles = Array.isArray(idTokenRoles) ? (idTokenRoles as string[]) : [];

      // Then try to get access token claims for roles
      let accessTokenRoles: string[] = [];
      try {
        const response = await instance.acquireTokenSilent({
          scopes: apiConfig.scopes,
          account: accounts[0],
        });

        if (response.accessToken) {
          accessTokenRoles = parseRolesFromJwt(response.accessToken);
        }
      } catch (error) {
        // Silent token acquisition failed, fall back to ID token roles only
      }

      // Merge and deduplicate
      const allRoles = [...new Set([...idRoles, ...accessTokenRoles])];
      setRoles(allRoles);
      setIsLoading(false);
    };

    extractRoles();
  }, [instance, accounts, inProgress]);

  return {
    roles,
    isAdmin: roles.includes('Admin'),
    isPowerUser: roles.includes('PowerUser'),
    canWrite: roles.includes('Admin') || roles.includes('PowerUser'),
    isLoading,
  };
}
